// Which renderer the document viewer uses for a file.
//
// The extension wins over mime_type: saved decks, documents and scripts are
// often stored as application/octet-stream.
//
//   pdf      - pdf.js, from the original bytes
//   office   - pdf.js, from the backend's LibreOffice PDF preview
//   sheet    - grid (SheetJS) with sheet tabs; xlsx/xls/ods can also show the PDF print layout
//   image / markdown / text / html / video / audio - native renderers
//   none     - no preview, download only

const EXTENSION_KINDS = {
  pdf: 'pdf',
  docx: 'office', doc: 'office', pptx: 'office', ppt: 'office', odt: 'office', odp: 'office', rtf: 'office',
  xlsx: 'sheet', xlsm: 'sheet', xls: 'sheet', ods: 'sheet', csv: 'sheet', tsv: 'sheet',
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', svg: 'image', bmp: 'image', avif: 'image', ico: 'image',
  md: 'markdown', markdown: 'markdown',
  txt: 'text', log: 'text', json: 'text', geojson: 'text', xml: 'text', yaml: 'text', yml: 'text',
  py: 'text', js: 'text', jsx: 'text', ts: 'text', tsx: 'text', sql: 'text', sh: 'text', css: 'text',
  ics: 'text', vcf: 'text', eml: 'text', kml: 'text',
  html: 'html', htm: 'html',
  mp4: 'video', webm: 'video', mov: 'video', m4v: 'video',
  mp3: 'audio', wav: 'audio', m4a: 'audio', ogg: 'audio',
};

// Formats with a LibreOffice PDF preview on the backend (/preview).
const PREVIEWABLE_SHEETS = new Set(['xlsx', 'xlsm', 'xls', 'ods']);

const KIND_LABELS = {
  docx: 'Word document', doc: 'Word document', odt: 'Document', rtf: 'Rich text',
  pptx: 'PowerPoint', ppt: 'PowerPoint', odp: 'Presentation',
  xlsx: 'Excel workbook', xlsm: 'Excel workbook', xls: 'Excel workbook', ods: 'Spreadsheet',
  csv: 'CSV', tsv: 'TSV', pdf: 'PDF', md: 'Markdown', json: 'JSON', html: 'HTML page',
};

export function fileExtension(filename) {
  const match = /\.([a-z0-9]+)$/i.exec(String(filename || '').trim());
  return match ? match[1].toLowerCase() : '';
}

function kindFromMime(mimeType) {
  const mime = String(mimeType || '').split(';')[0].trim().toLowerCase();
  if (!mime || mime === 'application/octet-stream') return 'none';
  if (mime === 'application/pdf') return 'pdf';
  if (mime === 'image/heic' || mime === 'image/heif' || mime === 'image/tiff') return 'none';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.includes('spreadsheetml') || mime === 'application/vnd.ms-excel' || mime === 'text/csv') return 'sheet';
  if (mime.includes('wordprocessingml') || mime.includes('presentationml') || mime === 'application/msword'
    || mime === 'application/vnd.ms-powerpoint') return 'office';
  if (mime === 'text/markdown') return 'markdown';
  if (mime === 'text/html') return 'html';
  if (mime.startsWith('text/') || mime === 'application/json' || mime.endsWith('+json') || mime.endsWith('+xml')) return 'text';
  return 'none';
}

export function viewerKind(file) {
  const ext = fileExtension(file?.filename);
  return EXTENSION_KINDS[ext] || kindFromMime(file?.mime_type);
}

/** True when the backend can render this sheet as a PDF print layout. */
export function hasPrintLayout(file) {
  return PREVIEWABLE_SHEETS.has(fileExtension(file?.filename));
}

export function kindLabel(file) {
  const ext = fileExtension(file?.filename);
  if (KIND_LABELS[ext]) return KIND_LABELS[ext];
  const kind = viewerKind(file);
  if (kind === 'image') return 'Image';
  if (kind === 'video') return 'Video';
  if (kind === 'audio') return 'Audio';
  return ext ? ext.toUpperCase() : 'File';
}

// Library buckets: the File Browser's kind filter and the icon a file gets
// everywhere (chat chips, viewer tabs, browser rows). Same buckets as the
// backend's `kind` field on /v1/vfs/files rows.
export const LIBRARY_KINDS = ['pdf', 'spreadsheet', 'document', 'presentation', 'image', 'other'];

export const LIBRARY_KIND_LABELS = {
  pdf: 'PDF',
  spreadsheet: 'Spreadsheet',
  document: 'Document',
  presentation: 'Slides',
  image: 'Image',
  other: 'File',
};

const LIBRARY_KIND_BY_EXT = {
  pdf: 'pdf',
  xlsx: 'spreadsheet', xlsm: 'spreadsheet', xls: 'spreadsheet', ods: 'spreadsheet', csv: 'spreadsheet', tsv: 'spreadsheet',
  docx: 'document', doc: 'document', odt: 'document', rtf: 'document', txt: 'document', md: 'document', markdown: 'document',
  pptx: 'presentation', ppt: 'presentation', odp: 'presentation', key: 'presentation',
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', svg: 'image', bmp: 'image',
  avif: 'image', heic: 'image', heif: 'image', tif: 'image', tiff: 'image',
};

function libraryKindFromMime(mimeType) {
  const mime = String(mimeType || '').split(';')[0].trim().toLowerCase();
  if (!mime) return 'other';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('image/')) return 'image';
  if (mime.includes('spreadsheetml') || mime.includes('excel') || mime === 'text/csv'
    || mime === 'text/tab-separated-values') return 'spreadsheet';
  if (mime.includes('presentationml') || mime.includes('powerpoint')) return 'presentation';
  if (mime.includes('wordprocessingml') || mime === 'application/msword' || mime === 'application/rtf'
    || mime === 'text/plain' || mime === 'text/markdown') return 'document';
  return 'other';
}

/** pdf | spreadsheet | document | presentation | image | other */
export function libraryKind(file) {
  if (file?.kind && LIBRARY_KIND_LABELS[file.kind]) return file.kind;
  const ext = fileExtension(file?.filename || file?.name);
  return LIBRARY_KIND_BY_EXT[ext] || libraryKindFromMime(file?.mime_type || file?.mimeType);
}
